/** Loading and reading the packed sprite atlas.
 *
 * The manifest is fetched once and shared through context. Individual sheets are
 * never decoded here: a DOM `background-image` lets the browser decode and cache
 * a 4096x4096 PNG far better than any hand-rolled loader, and the world canvas
 * (owned by `packages/render`) has its own upload path.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Atlas, AtlasFrame, AtlasManifest } from "../theme/atlas.js";

const EMPTY_MANIFEST: AtlasManifest = { sheets: [], keys: {} };

/**
 * Wrap a manifest as an `Atlas`. Pure: `baseUrl` is joined textually, so this
 * works in a test with no network.
 */
export function createAtlas(manifest: AtlasManifest, baseUrl: string): Atlas {
  const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const byId = new Map(manifest.sheets.map((s) => [s.id, s]));
  return {
    manifest,
    sheetUrl(sheetId: string): string {
      const sheet = byId.get(sheetId);
      return sheet === undefined ? "" : `${base}/${sheet.file}`;
    },
    frame(key: string): AtlasFrame | null {
      return manifest.keys[key] ?? null;
    },
    has(key: string): boolean {
      return manifest.keys[key] !== undefined;
    },
  };
}

export const EMPTY_ATLAS: Atlas = createAtlas(EMPTY_MANIFEST, "");

export const AtlasContext = createContext<Atlas>(EMPTY_ATLAS);

/** Never null: a missing atlas draws the hatched "missing frame" swatch rather
 *  than crashing a panel, because a renderer must degrade, not fail. */
export function useAtlas(): Atlas {
  return useContext(AtlasContext);
}

export interface AtlasLoadState {
  readonly atlas: Atlas;
  readonly loading: boolean;
  readonly error: string | null;
}

/**
 * Fetch `<baseUrl>/assets.json` once. Intended to be called by the app shell and
 * pushed into `AtlasContext`; panels use `useAtlas`.
 */
export function useAtlasLoader(baseUrl: string): AtlasLoadState {
  const [manifest, setManifest] = useState<AtlasManifest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setManifest(null);
    setError(null);
    const load = async (): Promise<void> => {
      const response = await fetch(`${baseUrl.replace(/\/$/, "")}/assets.json`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // The manifest is build output from `tools/pack-assets.ts` and is trusted
      // to match `AtlasManifest`; a mismatch surfaces as a missing frame, which
      // draws the hatched swatch rather than crashing a panel.
      const json = (await response.json()) as AtlasManifest;
      if (live) setManifest(json);
    };
    void load().catch((e: unknown) => {
      if (live) setError(e instanceof Error ? e.message : String(e));
    });
    return () => { live = false; };
  }, [baseUrl]);

  const atlas = useMemo(
    () => (manifest === null ? EMPTY_ATLAS : createAtlas(manifest, baseUrl)),
    [manifest, baseUrl],
  );
  return { atlas, loading: manifest === null && error === null, error };
}
